/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useRef, useState } from 'react';
import axiosInstance from 'utils/axiosInterceptors';
import {
  TProductBrandDetails,
  TProductDetails
} from 'utils/types';
import ReactSelect from 'components/base/ReactSelect';
import CategorySelect from 'components/shared/common/CategorySelect';
import { Button, Card, Col, Form, Row } from 'react-bootstrap';
import useDebounce from 'hooks/useDebounce';
import { unitOptions } from 'utils/unitOptions';
import ImageModal, { ImageModalHandle } from 'components/modals/ImageModal';
import brandLogo from 'assets/img/icons/kandj.png';
import * as yup from 'yup';
import { toast } from 'react-toastify';
import { AxiosError } from 'axios';
import { toastConfig } from 'utils/toastConfig';

type Product = {
  productId: number;
  quantity: number;
  unitOfMeasure: string;
  imageUrl: string | null;
  googleFileId?: string | null;
};

interface ProductSet {
  ProductBrandId: number;
  ProductCategoryId: number;
  ProductName: string;
  Description: string;
  SellingPrice: string;
  BarcodeLabel: string;
  Images: FileList | null;
  Components: Product[];
  IsIndividualSaleOfComponentsNotAllowed: boolean;
}

export type SelectOption = {
  label: string;
  value: number;
};

const ProductSet = () => {
  const [products, setProducts] = useState<TProductDetails[]>([]);
  const [productBrands, setProductBrands] = useState<TProductBrandDetails[]>(
    []
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedComponent, setSelectedComponent] = useState<any[]>([]);
  const [isMultiple, setIsMultiple] = useState(false);
  const [productSet, setProductSet] = useState<ProductSet>({
    ProductBrandId: 0,
    ProductCategoryId: 0,
    ProductName: '',
    Description: '',
    SellingPrice: '',
    BarcodeLabel: '',
    Images: null,
    Components: [],
    IsIndividualSaleOfComponentsNotAllowed: false
  });
  const [inputs, setInputs] = useState({
    product: '',
    productBrand: ''
  });
  const [selectedBrand, setSelectedBrand] = useState<SelectOption | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const debouncedInputs = {
    product: useDebounce(inputs.product, 500),
    productBrand: useDebounce(inputs.productBrand, 500)
  };
  const imageModalRef = useRef<ImageModalHandle>(null);
  const [selectedImage, setSelectedImage] = useState<string | null>();
  const [selectedGoogleFileId, setSelectedGoogleFileId] = useState<
    string | undefined
  >();

  const openImageModal = (image: string | null, googleFileId?: string | null) => {
    setSelectedImage(image);
    setSelectedGoogleFileId(googleFileId || undefined);
    imageModalRef.current?.show();
  };

  const fetchData = async (
    endpoint: string,
    setter: React.Dispatch<any>,
    query: string = '',
    extraParams: string = ''
  ) => {
    setIsLoading(true);
    try {
      const res = await axiosInstance.get(
        `/api/v1/${endpoint}?page=1&pageSize=10&search=${query}${extraParams}`
      );
      setter(res.data.data);
    } finally {
      setIsLoading(false);
    }
  };

  const formatOptions = (data: any[], labelKey: string, valueKey: string) =>
    data.map(item => ({
      label: item[labelKey],
      value: item[valueKey]
    }));

  const formattedProductOptions = React.useMemo(
    () => formatOptions(products, 'name', 'productId'),
    [products]
  );

  const formattedProductBrandOptions = React.useMemo(
    () => formatOptions(productBrands, 'name', 'productBrandId'),
    [productBrands]
  );

  const handleChange = async (newValue: unknown) => {
    if (!Array.isArray(newValue)) {
      return;
    }

    setIsLoading(true);

    const updatedComponents = await Promise.all(
      newValue.map(async (option: any) => {
        const existingComponent = productSet.Components.find(
          component => component.productId === option.value
        );

        // Preserve existing component data if it already exists
        if (existingComponent) {
          return existingComponent;
        }

        // Otherwise, fetch the new component details
        const product = products.find(p => p.productId === option.value);
        let imageUrl = null;

        if (product?.imageFileIds?.length) {
          const googleFileId = product.imageFileIds[0]; // Use the first image ID
          try {
            const res = await axiosInstance.get(
              `/api/v1/products/images/${googleFileId}`,
              {
                responseType: 'blob'
              }
            );
            imageUrl = URL.createObjectURL(res.data);
          } catch (error) {
            console.error('Failed to fetch image:', error);
          }
        }

        return {
          productId: option.value,
          quantity: 1,
          unitOfMeasure: 'Piece',
          imageUrl: imageUrl || brandLogo,
          googleFileId: product?.imageFileIds?.[0] || null
        };
      })
    );

    setSelectedComponent(newValue);
    setProductSet(prev => ({
      ...prev,
      Components: updatedComponents
    }));

    setIsLoading(false);
  };

  const handleInputChange = (field: keyof typeof inputs, value: string) => {
    setInputs(prev => ({ ...prev, [field]: value }));
  };

  const handleFormChange = <K extends keyof ProductSet>(
    field: K,
    value: ProductSet[K]
  ) => {
    setProductSet(prev => ({ ...prev, [field]: value }));
  };

  const handleComponentChange = (
    index: number,
    field: keyof Product,
    value: any
  ) => {
    setProductSet(prevState => {
      const updatedComponents = [...prevState.Components];
      updatedComponents[index] = {
        ...updatedComponents[index],
        [field]: value
      };
      return { ...prevState, Components: updatedComponents };
    });
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    handleFormChange('Images', files);
  };

  const handleSubmitProductSet = async () => {
    const formData = new FormData();
    formData.append('ProductBrandId', productSet.ProductBrandId.toString());
    formData.append(
      'ProductCategoryId',
      productSet.ProductCategoryId.toString()
    );
    formData.append('ProductName', productSet.ProductName);
    formData.append('Description', productSet.Description);
    formData.append('SellingPrice', productSet.SellingPrice.toString());
    formData.append('BarcodeLabel', productSet.BarcodeLabel);
    formData.append(
      'IsIndividualSaleOfComponentsNotAllowed',
      productSet.IsIndividualSaleOfComponentsNotAllowed.toString()
    );

    if (productSet.Images && productSet.Images.length > 0) {
      for (let i = 0; i < productSet.Images.length; i++) {
        formData.append('Images', productSet.Images[i]);
      }
    }

    const filteredComponents = productSet.Components.map(
      ({ productId, quantity, unitOfMeasure }) => ({
        productId,
        quantity,
        unitOfMeasure
      })
    );

    formData.append('Components', JSON.stringify(filteredComponents));

    try {
      await productSetSchema.validate(productSet, { abortEarly: false });
      setIsSubmitting(true);
      await toast.promise(
        axiosInstance.post('/api/v1/productSets', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        }),
        {
          pending: 'Creating product set...',
          success: 'Product set created successfully!',
          error: {
            render({ data }) {
              const error = data as AxiosError;
              return error.message;
            }
          }
        },
        { ...toastConfig, autoClose: 3000 }
      );
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      setProductSet(resetForm());
      setSelectedComponent([]);
      setSelectedBrand(null);
      setIsSubmitting(false);
    } catch (error) {
      setIsSubmitting(false);
      if (error instanceof yup.ValidationError) {
        error.inner.forEach(err => {
          toast.error(err.message, { ...toastConfig, autoClose: 3000 });
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const productSetSchema = yup.object().shape({
    ProductBrandId: yup
      .number()
      .required('Select brand')
      .min(1, 'Select Brand'),
    ProductCategoryId: yup
      .number()
      .required('Select Category')
      .min(1, 'Select Category'),
    ProductName: yup.string().required('Product name is required'),
    Description: yup.string().required('Description name is required'),
    SellingPrice: yup.string().required('Selling price is required'),
    BarcodeLabel: yup.string().required('Barcode Label is required'),
    Images: yup.mixed().required('At least one image is required'),
    Components: yup
      .array()
      .of(
        yup.object().shape({
          quantity: yup
            .number()
            .required('Quantity is required')
            .min(1, 'Quantity must be at least 1'),
          unitOfMeasure: yup.string().required('Unit of measure is required')
        })
      )
      .min(2, 'At least two component is required')
  });

  const resetForm = () => ({
    ProductBrandId: 0,
    ProductCategoryId: 0,
    ProductName: '',
    Description: '',
    SellingPrice: '',
    BarcodeLabel: '',
    Images: null,
    Components: [],
    IsIndividualSaleOfComponentsNotAllowed: false
  });

  useEffect(() => {
    fetchData('products', setProducts, debouncedInputs.product);
  }, [debouncedInputs.product]);

  useEffect(() => {
    fetchData('productBrands', setProductBrands, debouncedInputs.productBrand);
  }, [debouncedInputs.productBrand]);

  const handleSelectChange = (
    newValue: unknown,
    setState: React.Dispatch<React.SetStateAction<SelectOption | null>>,
    formKey: keyof ProductSet
  ) => {
    const selected = newValue as SelectOption;
    setState(selected);
    handleFormChange(formKey, selected.value);
  };

  return (
    <div>
      <ReactSelect
        value={selectedComponent}
        options={formattedProductOptions}
        placeholder="Select Product Components"
        isSearchable
        isMulti
        closeMenuOnSelect
        isClearable
        onChange={handleChange}
        isLoading={isLoading}
        onInputChange={newValue => handleInputChange('product', newValue)}
      />
      <div className="d-flex">
        <Card className="w-50 mt-5 p-5 me-5 h-100">
          <h4 className="mb-5">Product Set</h4>
          <Form>
            <Row className="mb-3">
              <Form.Group as={Col}>
                <Form.Label>Product Name</Form.Label>
                <Form.Control
                  value={productSet.ProductName || ''}
                  type="text"
                  onChange={e =>
                    handleFormChange('ProductName', e.target.value)
                  }
                />
              </Form.Group>
            </Row>
            <Row className="mb-3">
              <Form.Group as={Col}>
                <Form.Label>Product Brand</Form.Label>
                <ReactSelect
                  value={selectedBrand}
                  options={formattedProductBrandOptions}
                  placeholder="Select Product Brand"
                  isSearchable
                  closeMenuOnSelect
                  onChange={newValue =>
                    handleSelectChange(
                      newValue,
                      setSelectedBrand,
                      'ProductBrandId'
                    )
                  }
                  isLoading={isLoading}
                  onInputChange={newValue =>
                    handleInputChange('productBrand', newValue)
                  }
                />
              </Form.Group>
            </Row>
            <Row className="mb-3">
              <Form.Group as={Col}>
                <Form.Label>Product Category</Form.Label>
                <CategorySelect
                  leavesOnly
                  placeholder="Select Product Category"
                  value={productSet.ProductCategoryId || null}
                  onChange={id =>
                    handleFormChange('ProductCategoryId', id ?? 0)
                  }
                />
              </Form.Group>
            </Row>
            <Row className="mb-3">
              <Form.Group as={Col}>
                <Form.Label>Description</Form.Label>
                <Form.Control
                  value={productSet.Description || ''}
                  as="textarea"
                  rows={6}
                  onChange={e =>
                    handleFormChange('Description', e.target.value)
                  }
                />
              </Form.Group>
            </Row>
            <Row className="mb-3">
              <Form.Group as={Col}>
                <Form.Label>Selling Price</Form.Label>
                <Form.Control
                  value={productSet.SellingPrice || 0}
                  type="text"
                  onChange={e =>
                    handleFormChange('SellingPrice', e.target.value)
                  }
                />
              </Form.Group>
            </Row>
            <Row className="mb-3">
              <Form.Group as={Col}>
                <Form.Label>Barcode Label</Form.Label>
                <Form.Control
                  value={productSet.BarcodeLabel || ''}
                  type="text"
                  onChange={e =>
                    handleFormChange('BarcodeLabel', e.target.value)
                  }
                />
              </Form.Group>
            </Row>
            <Row className="mb-3">
              <Form.Group as={Col}>
                <Form.Label>Upload Image</Form.Label>
                <Form.Control
                  ref={fileInputRef}
                  type="file"
                  onChange={handleImageChange}
                  multiple={isMultiple}
                />
                <Form.Check
                  type="switch"
                  id="custom-switch"
                  label="Multiple files"
                  checked={isMultiple}
                  onChange={() => setIsMultiple(!isMultiple)}
                />
              </Form.Group>
            </Row>
            <Row className="mb-3">
              <Form.Group as={Col}>
                <Form.Label>Is individual component can be sold</Form.Label>
                <Form.Check
                  type="radio"
                  label="Yes"
                  value="true"
                  checked={
                    productSet.IsIndividualSaleOfComponentsNotAllowed === true
                  }
                  onChange={() =>
                    handleFormChange(
                      'IsIndividualSaleOfComponentsNotAllowed',
                      true
                    )
                  }
                />
                <Form.Check
                  type="radio"
                  label="No"
                  value="false"
                  checked={
                    productSet.IsIndividualSaleOfComponentsNotAllowed === false
                  }
                  onChange={() =>
                    handleFormChange(
                      'IsIndividualSaleOfComponentsNotAllowed',
                      false
                    )
                  }
                />
              </Form.Group>
            </Row>
            <Button
              className="w-100"
              onClick={handleSubmitProductSet}
              disabled={isSubmitting}
            >
              submit
            </Button>
          </Form>
        </Card>
        {selectedComponent.length !== 0 ? (
          <Card className="w-50 mt-5 p-5 mx-auto">
            <h4 className="mb-5">Product Components</h4>
            {selectedComponent.map((c, index) => (
              <div key={c.value} className="mb-5">
                <Card.Title as="h5">{c.label}</Card.Title>
                {productSet.Components[index].imageUrl?.length !== 0 ? (
                  <img
                    src={productSet.Components[index].imageUrl || brandLogo}
                    alt={c.label}
                    style={{ width: '100%', height: '20rem' }}
                    onClick={() =>
                      openImageModal(
                        productSet.Components[index].imageUrl &&
                          productSet.Components[index].imageUrl,
                        productSet.Components[index].googleFileId
                      )
                    }
                  />
                ) : (
                  <img
                    src={brandLogo}
                    alt={`Product Image ${index}`}
                    style={{ width: '100%', height: '20rem' }}
                  />
                )}
                <Form.Label>Unit of Measure</Form.Label>
                <Form.Select
                  value={productSet.Components[index].unitOfMeasure}
                  onChange={e =>
                    handleComponentChange(
                      index,
                      'unitOfMeasure',
                      e.target.value
                    )
                  }
                >
                  {unitOptions.map(opt => (
                    <option key={opt.label} value={opt.label}>
                      {opt.label}
                    </option>
                  ))}
                </Form.Select>
                <Form.Label>Quantity</Form.Label>
                <Form.Control
                  type="number"
                  value={productSet.Components[index].quantity}
                  onChange={e =>
                    handleComponentChange(index, 'quantity', e.target.value)
                  }
                />
                <hr />
              </div>
            ))}
          </Card>
        ) : (
          <Card className="w-50 mt-5 p-5 mx-auto">
            <h4>No selected components...</h4>
          </Card>
        )}
      </div>
      <ImageModal
        size="lg"
        image={selectedImage || ''}
        googleFileId={selectedGoogleFileId}
        caption="Product photo"
        ref={imageModalRef}
      />
    </div>
  );
};

export default ProductSet;
